import { createSelector } from '@reduxjs/toolkit';
import {
    boardsSelectByQuery,
    taskColumnSelectByQuery,
    tasksSelectByQuery,
} from '@common-reducers/DBServiceThunks';
import type { CommonRootState } from '@common/types/common-root-state-type';
import { audienceMemberNames, audiencePreviewIds } from './destination-plan';

const EMPTY: Readonly<Record<string, string[]>> = Object.freeze({});

/**
 * The first five WhatsApp ids of every audience whose rows are ALREADY in the
 * store. One memoised pass shared by every row and the hover card — it reruns
 * only when boards, rows or columns change, never per row and never per render.
 *
 * It NEVER fetches. An audience nobody has opened has no rows here; its faces
 * come from the batched preview read in audience-preview-loader.ts instead
 * (useCampaignDestination merges the two, store rows first).
 *
 * Each reducer is read defensively: the extension and the website register
 * different sets of slices, and an unregistered one is `undefined`.
 */
export const selectAudiencePreviewIds = createSelector(
    [
        (state: CommonRootState) => (state as any).DBBoardsReducer,
        (state: CommonRootState) => (state as any).DBTasksReducer,
        (state: CommonRootState) => (state as any).DBTaskColumnReducer,
    ],
    (boards, tasks, columns): Readonly<Record<string, string[]>> => {
        if (!boards || !tasks || !columns) return EMPTY;

        const audienceIds = (boardsSelectByQuery(boards, { type: 'audience', deleted: { $ne: true } }, ['_id']) as any[])
            .map(b => String(b?._id ?? ''))
            .filter(Boolean);
        if (!audienceIds.length) return EMPTY;

        const waColumns = new Map<string, Set<string>>();
        for (const col of taskColumnSelectByQuery(columns, { boardId: { $in: audienceIds }, type: 'whatsapp-id-cell' }) as any[]) {
            if (!col?._id || !col?.boardId || col.deleted) continue;
            const set = waColumns.get(String(col.boardId)) ?? new Set<string>();
            set.add(String(col._id));
            waColumns.set(String(col.boardId), set);
        }
        if (!waColumns.size) return EMPTY;

        const rows = tasksSelectByQuery(tasks, { boardId: { $in: [...waColumns.keys()] } }) as any[];
        return audiencePreviewIds(rows, waColumns);
    },
);

const NO_NAMES: Readonly<Record<string, string>> = Object.freeze({});

/**
 * The names of audience members whose rows are ALREADY in the store, keyed by
 * canonical id — for the member list. Same inputs and the same memoised,
 * never-fetching discipline as `selectAudiencePreviewIds` above.
 */
export const selectAudienceMemberNames = createSelector(
    [
        (state: CommonRootState) => (state as any).DBBoardsReducer,
        (state: CommonRootState) => (state as any).DBTasksReducer,
        (state: CommonRootState) => (state as any).DBTaskColumnReducer,
    ],
    (boards, tasks, columns): Readonly<Record<string, string>> => {
        if (!boards || !tasks || !columns) return NO_NAMES;
        const audienceIds = (boardsSelectByQuery(boards, { type: 'audience', deleted: { $ne: true } }, ['_id']) as any[])
            .map(b => String(b?._id ?? ''))
            .filter(Boolean);
        if (!audienceIds.length) return NO_NAMES;
        const waColumns = new Map<string, Set<string>>();
        for (const col of taskColumnSelectByQuery(columns, { boardId: { $in: audienceIds }, type: 'whatsapp-id-cell' }) as any[]) {
            if (!col?._id || !col?.boardId || col.deleted) continue;
            const set = waColumns.get(String(col.boardId)) ?? new Set<string>();
            set.add(String(col._id));
            waColumns.set(String(col.boardId), set);
        }
        if (!waColumns.size) return NO_NAMES;
        const rows = tasksSelectByQuery(tasks, { boardId: { $in: [...waColumns.keys()] } }) as any[];
        const names = audienceMemberNames(rows, waColumns);
        return Object.keys(names).length ? names : NO_NAMES;
    },
);
